// Hooks personalizados para espacios y reservas
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toastError, toastSuccess, toastLoading, toastDismiss } from '../utils/toastHelper'

import espaciosService from '../services/espaciosService'
import type {
  ConsultaDisponibilidad,
  ReservaInvitadoCreate,
} from '../types'
import type {
  CheckoutDisponibilidad,
  FiltrosDisponibilidad,
  RangoFechas,
} from '@/types/availability'

// Query keys
export const QUERY_KEYS = {
  ESPACIOS: 'espacios',
  DISPONIBILIDAD: 'disponibilidad',
  RESERVA: 'reserva',
  RESERVAS: 'reservas',
  // Rev2 Disponibilidad endpoints (activos)
  REV_DISPONIBILIDAD_ESPACIO: 'revDisponibilidadEspacio',
  REV_DISPONIBILIDAD_CHECKOUT: 'revDisponibilidadCheckout',
} as const

// Hook para obtener espacios (público)
export const useEspacios = (filtros?: any) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ESPACIOS, filtros],
    queryFn: async () => {
      const response = await espaciosService.listarEspacios(filtros);
      // Manejar ambos casos: array directo o PaginatedResponse
      if (Array.isArray(response)) {
        return response;
      }
      return response?.items || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    // ✅ CRÍTICO: Mantener la referencia del array mientras los datos sean iguales
    structuralSharing: true,
  })
}

// Hook para obtener una reserva específica
export const useReserva = (reservaId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.RESERVA, reservaId],
    queryFn: () => espaciosService.obtenerReserva(reservaId),
    enabled: !!reservaId,
  })
}

// Hook para crear reserva como invitado
export const useCrearReservaInvitado = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (reserva: ReservaInvitadoCreate) =>
      espaciosService.crearReservaInvitado(reserva),
    onSuccess: (data) => {
      toastSuccess('¡Reserva creada exitosamente!')

      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ESPACIOS] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DISPONIBILIDAD] })

      return data
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Error al crear la reserva'
      toastError(message)
      throw error
    },
  })
}

// ============================================================================
// REV2 DISPONIBILIDAD HOOKS
// ============================================================================

/**
 * Hook para obtener disponibilidad simple (sin tarifas)
 * Endpoint: GET /api/espacios/disponibilidad_simple
 * Uso: SpacesByTypePageV2 - grid de espacios sin precios
 */
export const useDisponibilidadSimple = (filtros: FiltrosDisponibilidad | null) => {
  return useQuery({
    queryKey: ['disponibilidadSimple', filtros],
    queryFn: () =>
      espaciosService.disponibilidadSimple(
        filtros!.fecha_entrada,
        filtros!.fecha_salida,
        filtros!.tipo_espacio_id
      ),
    enabled: !!(filtros && filtros.fecha_entrada && filtros.fecha_salida),
    staleTime: 5 * 60 * 1000, // 5 minutos
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData, // ✅ Mantener datos anteriores mientras carga nuevos
  })
}

/**
 * Hook para obtener ocupación panorámica - TODOS los espacios
 * Endpoint: GET /api/espacios/rev_ocupacion_panoramica
 * Uso: HomePage - mostrar todos los espacios con disponibilidad marcada
 */
export const useOcupacionPanoramica = (filtros: FiltrosDisponibilidad | null) => {
  return useQuery({
    queryKey: ['ocupacionPanoramica', filtros],
    queryFn: () =>
      espaciosService.revOcupacionPanoramica(
        filtros!.fecha_entrada,
        filtros!.fecha_salida,
        filtros!.tipo_espacio_id
      ),
    enabled: !!(filtros && filtros.fecha_entrada && filtros.fecha_salida),
    staleTime: 2 * 60 * 1000, // 2 minutos
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData, // ✅ Mantener datos anteriores mientras carga nuevos
  })
}

/**
 * Hook para obtener disponibilidad detallada de un espacio específico
 * Endpoint: GET /api/espacios/rev_disponibilidad_espacio/{espacio_id}?fecha_entrada=X&fecha_salida=Y
 * Uso: DateAvailabilityModal - desglose con tarifas simple y multiple
 */
export const useDisponibilidadEspacio = (
  espacio_id: string | null,
  fechas: RangoFechas | null
) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REV_DISPONIBILIDAD_ESPACIO, espacio_id, fechas?.fecha_entrada, fechas?.fecha_salida],
    queryFn: () =>
      espaciosService.revDisponibilidadEspacio(
        espacio_id!,
        fechas!.fecha_entrada,
        fechas!.fecha_salida
      ),
    enabled: !!(
      espacio_id &&
      fechas &&
      fechas.fecha_entrada &&
      fechas.fecha_salida
    ),
    staleTime: 5 * 60 * 1000, // 5 minutos
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook para validación final de disponibilidad antes de checkout
 * Endpoint: GET /api/espacios/rev_disponibilidad_checkout/{espacio_id}?fecha_entrada=X&fecha_salida=Y&cantidad_unidades=N&bloqueo_temporal_id=ID
 * Uso: CheckoutPage - validación final + recálculo desglose
 */
export const useDisponibilidadCheckout = (
  espacio_id: string | null,
  fecha_entrada: string | null,
  fecha_salida: string | null,
  cantidad_unidades: number | null,
  bloqueo_temporal_id: string | null
) => {
  return useQuery({
    queryKey: [
      QUERY_KEYS.REV_DISPONIBILIDAD_CHECKOUT,
      espacio_id,
      fecha_entrada,
      fecha_salida,
      cantidad_unidades,
      bloqueo_temporal_id,
    ],
    queryFn: () =>
      espaciosService.revDisponibilidadCheckout(
        espacio_id!,
        fecha_entrada!,
        fecha_salida!,
        cantidad_unidades!,
        bloqueo_temporal_id!
      ),
    enabled: !!(
      espacio_id &&
      fecha_entrada &&
      fecha_salida &&
      cantidad_unidades &&
      bloqueo_temporal_id
    ),
    staleTime: 1 * 60 * 1000, // 1 minuto (validación final, cambios frecuentes)
    refetchOnWindowFocus: true, // Re-validar si usuario vuelve a pestaña
  })
}

/**
 * Hook para obtener imágenes de espacios específicos
 * Uso: Inyectar imágenes en grids de espacios
 */
export const useEspacioImages = (espacioIds?: string[]) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ESPACIOS, 'images', espacioIds],
    queryFn: async () => {
      if (!espacioIds || espacioIds.length === 0) return new Map();

      const response = await espaciosService.listarEspacios();
      const espacios = Array.isArray(response) ? response : response?.items || [];

      // Crear mapa: espacioId -> imagenes
      const imageMap = new Map<string, string[]>();
      espacios.forEach((espacio: any) => {
        if (espacioIds.includes(espacio.id) && espacio.imagenes) {
          imageMap.set(espacio.id, espacio.imagenes);
        }
      });

      return imageMap;
    },
    staleTime: 30 * 60 * 1000, // 30 minutos (imágenes no cambian frecuentemente)
    refetchOnWindowFocus: false,
    enabled: !!(espacioIds && espacioIds.length > 0),
  })
}

